import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface PublicThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const PublicThemeContext = createContext<PublicThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'crypxa_public_theme';

export const PublicThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get initial theme from localStorage or default to 'light' for public pages
  const getInitialTheme = (): Theme => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme;
      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }
    }
    // Default to light for public pages
    return 'light';
  };

  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Apply theme based on current route
  useEffect(() => {
    const applyPublicTheme = () => {
      const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
      
      if (!isDashboardRoute) {
        // On public pages, apply public theme
        const currentTheme = getInitialTheme();
        document.documentElement.setAttribute('data-theme', currentTheme);
        setThemeState(currentTheme);
      }
    };

    // Apply on mount
    applyPublicTheme();

    // Listen to route changes
    const handleRouteChange = () => {
      applyPublicTheme();
    };

    window.addEventListener('popstate', handleRouteChange);
    
    // Also listen to pushState/replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      setTimeout(applyPublicTheme, 0);
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      setTimeout(applyPublicTheme, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  useEffect(() => {
    // Only apply public theme if we're NOT on a dashboard route
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    
    if (!isDashboardRoute) {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    // Only apply if not on dashboard route
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    if (!isDashboardRoute) {
      document.documentElement.setAttribute('data-theme', newTheme);
    }
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <PublicThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </PublicThemeContext.Provider>
  );
};

export const usePublicTheme = () => {
  const context = useContext(PublicThemeContext);
  if (context === undefined) {
    throw new Error('usePublicTheme must be used within a PublicThemeProvider');
  }
  return context;
};

